// AI Sales Price Intelligence Tab
// VSTL Trade Manager - Phase 5

import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { DollarSign, Globe, AlertTriangle, RefreshCw, ShieldAlert, TrendingUp } from 'lucide-react';

const SalesIntelligenceTab = () => {
  const [mode, setMode] = useState('recommend');
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [result, setResult] = useState(null);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // Recommend form
  const [recSkuId, setRecSkuId] = useState('');
  const [recCountries, setRecCountries] = useState('');

  // Underprice check form
  const [upSkuId, setUpSkuId] = useState('');
  const [upPrice, setUpPrice] = useState('');
  const [upCountry, setUpCountry] = useState('USA');

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await api.get('/ai-engine/sales-intelligence/alerts?limit=20');
      setAlerts(res.data.data || []);
    } catch (e) {
      console.error('Failed to load sales alerts:', e);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const recommendPrices = async () => {
    if (!recSkuId) {
      toast.error('SKU ID is required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const countries = recCountries ? recCountries.split(',').map(c => c.trim()).filter(Boolean) : null;
      const res = await api.post('/ai-engine/sales-intelligence/recommend', {
        sku_id: recSkuId,
        countries
      });
      setResult(res.data);
      fetchAlerts();
      toast.success('Price recommendations generated');
    } catch (e) {
      toast.error('Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const checkUnderprice = async () => {
    if (!upSkuId || !upPrice) {
      toast.error('SKU and proposed price are required');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/ai-engine/sales-intelligence/check-underprice', {
        sku_id: upSkuId,
        proposed_price: parseFloat(upPrice),
        country: upCountry
      });
      setResult(res.data);
      fetchAlerts();
      toast.success('Underprice check complete');
    } catch (e) {
      toast.error('Failed to check price');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical': return <Badge className="bg-red-500 text-white">Critical</Badge>;
      case 'warning': return <Badge className="bg-yellow-500 text-white">Warning</Badge>;
      default: return <Badge className="bg-green-500 text-white">OK</Badge>;
    }
  };

  return (
    <div className="space-y-4" data-testid="sales-intelligence-tab">
      {/* Mode Toggle */}
      <div className="flex gap-2">
        <Button
          variant={mode === 'recommend' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setMode('recommend'); setResult(null); }}
          data-testid="recommend-mode-btn"
        >
          <Globe className="h-4 w-4 mr-1" /> Price Recommendations
        </Button>
        <Button
          variant={mode === 'underprice' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setMode('underprice'); setResult(null); }}
          data-testid="underprice-mode-btn"
        >
          <ShieldAlert className="h-4 w-4 mr-1" /> Underprice Check
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Input Form */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {mode === 'recommend' ? 'Country-wise Price Recommendations' : 'Check Underpriced Sale'}
            </CardTitle>
            <CardDescription>
              {mode === 'recommend'
                ? 'Get AI-recommended selling prices per target market'
                : 'Check if a proposed selling price is too low'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode === 'recommend' ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">SKU ID</Label>
                  <Input value={recSkuId} onChange={e => setRecSkuId(e.target.value)} placeholder="e.g. 1.2TB SAS HDD" data-testid="rec-sku-input" />
                </div>
                <div>
                  <Label className="text-xs">Target Countries (optional, comma-separated)</Label>
                  <Input
                    value={recCountries}
                    onChange={e => setRecCountries(e.target.value)}
                    placeholder="USA, UK, Germany, India..."
                    data-testid="rec-countries-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Leave blank for all default markets</p>
                </div>
                <Button onClick={recommendPrices} disabled={loading} className="w-full" data-testid="generate-recommendations-btn">
                  {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <TrendingUp className="h-4 w-4 mr-2" />}
                  {loading ? 'Analyzing...' : 'Get Recommendations'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">SKU ID</Label>
                    <Input value={upSkuId} onChange={e => setUpSkuId(e.target.value)} placeholder="e.g. 1.2TB SAS HDD" data-testid="up-sku-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Proposed Sell Price ($)</Label>
                    <Input type="number" value={upPrice} onChange={e => setUpPrice(e.target.value)} placeholder="0.00" data-testid="up-price-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Target Country</Label>
                  <select
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={upCountry}
                    onChange={e => setUpCountry(e.target.value)}
                    data-testid="up-country-select"
                  >
                    {["USA", "UK", "Germany", "Netherlands", "India", "UAE", "Singapore", "Australia"].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <Button onClick={checkUnderprice} disabled={loading} className="w-full" data-testid="check-underprice-btn">
                  {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <ShieldAlert className="h-4 w-4 mr-2" />}
                  {loading ? 'Checking...' : 'Check Price'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Result Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">AI Output</CardTitle>
          </CardHeader>
          <CardContent>
            {!result ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p>Results will appear here</p>
              </div>
            ) : result.type === 'price_recommendation' ? (
              <div className="space-y-3 max-h-[400px] overflow-y-auto" data-testid="price-recommendation-result">
                {result.pricing_strategy && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Pricing Strategy</p>
                    <p className="text-sm">{result.pricing_strategy}</p>
                  </div>
                )}
                {result.underpriced_warning && (
                  <div className="p-3 border border-red-200 bg-red-50 rounded-lg">
                    <p className="text-xs text-red-700 font-semibold flex items-center gap-1 mb-1">
                      <AlertTriangle className="h-3 w-3" /> Underpriced Warning
                    </p>
                    <p className="text-sm text-red-800">{result.warning_details}</p>
                  </div>
                )}
                {result.recommended_prices?.length > 0 && (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Country</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Range</TableHead>
                          <TableHead>Margin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.recommended_prices.map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="font-medium text-sm">{p.country}</TableCell>
                            <TableCell className="text-sm font-semibold text-green-600">
                              ${p.recommended_price?.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              ${p.price_range_min?.toFixed(2)} - ${p.price_range_max?.toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {p.margin_percent?.toFixed(1)}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {result.market_notes && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Market Notes</p>
                    <p className="text-sm">{result.market_notes}</p>
                  </div>
                )}
              </div>
            ) : result.type === 'underprice_check' ? (
              <div className="space-y-3" data-testid="underprice-check-result">
                <div className="flex items-center gap-3">
                  {result.is_underpriced
                    ? <Badge className="bg-red-500 text-white">Underpriced</Badge>
                    : <Badge className="bg-green-500 text-white">Price OK</Badge>
                  }
                  {result.severity && getSeverityBadge(result.severity)}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Proposed Price</p>
                    <p className="text-sm font-semibold">${result.proposed_price?.toFixed(2)}</p>
                    {result.margin_at_proposed !== undefined && (
                      <p className="text-xs text-muted-foreground">Margin: {result.margin_at_proposed?.toFixed(1)}%</p>
                    )}
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Recommended Min</p>
                    <p className="text-sm font-semibold text-green-600">
                      ${result.minimum_recommended_price?.toFixed(2) || 'N/A'}
                    </p>
                    {result.margin_at_recommended !== undefined && (
                      <p className="text-xs text-muted-foreground">Margin: {result.margin_at_recommended?.toFixed(1)}%</p>
                    )}
                  </div>
                </div>
                {result.explanation && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Analysis</p>
                    <p className="text-sm">{result.explanation}</p>
                  </div>
                )}
                {result.recommendation && (
                  <div className="p-3 border rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold mb-1">Recommendation</p>
                    <p className="text-sm">{result.recommendation}</p>
                  </div>
                )}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* History / Alerts */}
      {alerts.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent Sales Intelligence</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchAlerts}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {alerts.slice(0, 10).map((item) => (
                    <TableRow key={item.id} data-testid={`sales-row-${item.id}`}>
                      <TableCell className="text-xs">{new Date(item.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {item.type === 'price_recommendation' ? 'Pricing' : 'Underprice Check'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{item.sku_id}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {item.type === 'price_recommendation'
                          ? `${item.recommended_prices?.length || 0} markets`
                          : `${item.is_underpriced ? 'UNDERPRICED' : 'OK'} - $${item.proposed_price?.toFixed(2)}`
                        }
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedAlert(item)}>View</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alert Detail Modal */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedAlert?.type === 'price_recommendation' ? 'Price Recommendation Details' : 'Underprice Check Details'}
            </DialogTitle>
            <DialogDescription>{selectedAlert?.sku_id}</DialogDescription>
          </DialogHeader>
          {selectedAlert && selectedAlert.type === 'price_recommendation' && (
            <div className="space-y-3">
              {selectedAlert.pricing_strategy && (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs font-semibold mb-1">Pricing Strategy</p>
                  <p className="text-sm">{selectedAlert.pricing_strategy}</p>
                </div>
              )}
              {selectedAlert.recommended_prices?.length > 0 && (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Country</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Range</TableHead>
                        <TableHead>Margin</TableHead>
                        <TableHead>Rationale</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedAlert.recommended_prices.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{p.country}</TableCell>
                          <TableCell className="font-semibold text-green-600">${p.recommended_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-xs">${p.price_range_min?.toFixed(2)} - ${p.price_range_max?.toFixed(2)}</TableCell>
                          <TableCell>{p.margin_percent?.toFixed(1)}%</TableCell>
                          <TableCell className="text-xs max-w-[150px] truncate">{p.rationale}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          {selectedAlert && selectedAlert.type === 'underprice_check' && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                {selectedAlert.is_underpriced
                  ? <Badge className="bg-red-500 text-white">Underpriced</Badge>
                  : <Badge className="bg-green-500 text-white">OK</Badge>
                }
                {getSeverityBadge(selectedAlert.severity)}
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs font-semibold mb-1">Analysis</p>
                <p className="text-sm">{selectedAlert.explanation}</p>
              </div>
              <div className="p-3 border rounded-lg">
                <p className="text-xs font-semibold mb-1">Recommendation</p>
                <p className="text-sm">{selectedAlert.recommendation}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesIntelligenceTab;
